import { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { DisputeOutcomeEntry } from '@/lib/disputes';
import { disputeTagLabel } from '@/lib/disputes';

function firstInitial(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[1][0]}.`;
  return parts[0];
}

function outcomeLabel(outcome: string, t: (key: string) => string): string {
  switch (outcome) {
    case 'accepted':           return t('disputeOutcomeCard.outcomeLabel_accepted');
    case 'admit_fault':        return t('disputeOutcomeCard.outcomeLabel_accepted');
    case 'penalty_respondent': return t('disputeOutcomeCard.outcomeLabel_penaltyRespondent');
    case 'auto_cancelled':     return t('disputeOutcomeCard.outcomeLabel_autoCancelled');
    case 'timeout_default':    return t('disputeOutcomeCard.outcomeLabel_timeoutDefault');
    case 'baseless_complaint': return t('disputeOutcomeCard.outcomeLabel_baselessComplaint');
    case 'jury_favor_initiator':
    case 'jury_favor_accused':  return t('disputeOutcomeCard.outcomeLabel_juryVerdict');
    case 'jury_no_quorum':      return t('disputeOutcomeCard.outcomeLabel_adminReview');
    default:                   return t('disputeOutcomeCard.outcomeLabel_default');
  }
}

type Verdict = { text: string; kind: 'right' | 'wrong' | 'neutral' } | null;

/** Who was vindicated per dispute outcome — shown under each side. */
function disputeVerdict(outcome: string, side: 'initiator' | 'respondent', t: (key: string) => string): Verdict {
  switch (outcome) {
    case 'accepted':
    case 'admit_fault':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_right'), kind: 'right' }
        : { text: t('disputeOutcomeCard.verdict_admitFault'), kind: 'wrong' };
    case 'penalty_respondent':
    case 'penalty_respondent_auto':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_right'), kind: 'right' }
        : { text: t('disputeOutcomeCard.verdict_atFault'), kind: 'wrong' };
    case 'auto_cancelled':
    case 'timeout_default':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_right'), kind: 'right' }
        : { text: t('disputeOutcomeCard.verdict_noResponse'), kind: 'wrong' };
    case 'baseless_complaint':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_baseless'), kind: 'wrong' }
        : { text: t('disputeOutcomeCard.verdict_right'), kind: 'right' };
    case 'withdrawn':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_withdrawn'), kind: 'neutral' }
        : null;
    case 'mutual_resolution':
      return { text: t('disputeOutcomeCard.verdict_mutual'), kind: 'neutral' };
    case 'deadlock':
      return { text: t('disputeOutcomeCard.verdict_deadlock'), kind: 'neutral' };
    case 'jury_favor_initiator':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_right'), kind: 'right' }
        : { text: t('disputeOutcomeCard.verdict_atFault'), kind: 'wrong' };
    case 'jury_favor_accused':
      return side === 'initiator'
        ? { text: t('disputeOutcomeCard.verdict_baseless'), kind: 'wrong' }
        : { text: t('disputeOutcomeCard.verdict_right'), kind: 'right' };
    case 'jury_no_quorum':
      return { text: t('disputeOutcomeCard.verdict_pendingAdmin'), kind: 'neutral' };
    default:
      return null;
  }
}

type Props = {
  entry: DisputeOutcomeEntry;
  /** Όταν δίνεται (π.χ. στο επαγγελματικό/επιχειρησιακό προφίλ), εμφανίζεται
   * επιπλέον ρητή γραμμή "υπέρ της επιχείρησης/του πελάτη" όταν η διαφωνία
   * κρίθηκε από κληρωτούς κριτές — τα ids της ίδιας της επιχείρησης (ο
   * ιδιοκτήτης + τυχόν συνέταιροι) ώστε να ξέρουμε ποια πλευρά είναι αυτή. */
  businessMemberIds?: string[];
};

export function DisputeOutcomeCard({ entry, businessMemberIds }: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const initName = firstInitial(entry.initiator_name || t('disputeOutcomeCard.defaultUser'));
  const respName = firstInitial(entry.respondent_name || t('disputeOutcomeCard.defaultUser'));
  const initVerdict = disputeVerdict(entry.outcome, 'initiator', t);
  const respVerdict = disputeVerdict(entry.outcome, 'respondent', t);

  const isJuryOutcome = entry.outcome === 'jury_favor_initiator' || entry.outcome === 'jury_favor_accused';
  let juryFavorsBusiness: boolean | null = null;
  if (isJuryOutcome && businessMemberIds?.length) {
    const businessIsInitiator = businessMemberIds.includes(entry.initiator_id);
    const favoredInitiator = entry.outcome === 'jury_favor_initiator';
    juryFavorsBusiness = businessIsInitiator ? favoredInitiator : !favoredInitiator;
  }

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="alert-circle-outline" size={14} color={colors.warning} />
        <Text style={styles.headerTitle} numberOfLines={1}>
          {entry.agreement_title || t('disputeOutcomeCard.defaultAgreement')}
        </Text>
        <View style={styles.outcomeBadge}>
          <Text style={styles.outcomeBadgeText}>{outcomeLabel(entry.outcome, t)}</Text>
        </View>
      </View>

      {juryFavorsBusiness != null && (
        <View style={[styles.juryVerdictBanner, juryFavorsBusiness ? styles.juryVerdictFavor : styles.juryVerdictAgainst]}>
          <Ionicons
            name={juryFavorsBusiness ? 'checkmark-circle' : 'close-circle'}
            size={13}
            color={juryFavorsBusiness ? colors.success : colors.danger}
          />
          <Text style={[styles.juryVerdictText, { color: juryFavorsBusiness ? colors.success : colors.danger }]}>
            {juryFavorsBusiness ? t('disputeOutcomeCard.juryFavorsBusiness') : t('disputeOutcomeCard.juryFavorsClient')}
          </Text>
        </View>
      )}

      {/* Split tile */}
      <View style={styles.split}>
        {/* Initiator */}
        <View style={[styles.side, styles.sideLeft]}>
          <Text style={styles.sideName}>{initName}</Text>
          <Text style={styles.roleHint}>{t('dispute.initiator_role')}</Text>
          {!!initVerdict && (
            <View style={[styles.verdictPill, styles[`verdict_${initVerdict.kind}`]]}>
              <Text style={[styles.verdictText, styles[`verdictText_${initVerdict.kind}`]]}>{initVerdict.text}</Text>
            </View>
          )}
          {entry.initiator_tags.length > 0 ? (
            entry.initiator_tags.map((t) => (
              <View key={t} style={styles.tagPill}>
                <Text style={styles.tagText}>{disputeTagLabel(t)}</Text>
              </View>
            ))
          ) : (
            <Text style={styles.noTags}>—</Text>
          )}
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Respondent */}
        <View style={[styles.side, styles.sideRight]}>
          <Text style={styles.sideName}>{respName}</Text>
          <Text style={styles.roleHint}>{(entry.outcome === 'accepted' || entry.outcome === 'admit_fault') ? t('disputeOutcomeCard.respondentRole_admitted') : entry.outcome === 'timeout_default' ? t('disputeOutcomeCard.respondentRole_noResponse') : t('disputeOutcomeCard.respondentRole_responded')}</Text>
          {!!respVerdict && (
            <View style={[styles.verdictPill, styles[`verdict_${respVerdict.kind}`]]}>
              <Text style={[styles.verdictText, styles[`verdictText_${respVerdict.kind}`]]}>{respVerdict.text}</Text>
            </View>
          )}
          {entry.respondent_tags.length > 0 ? (
            entry.respondent_tags.map((t) => (
              <View key={t} style={styles.tagPill}>
                <Text style={styles.tagText}>{disputeTagLabel(t)}</Text>
              </View>
            ))
          ) : (
            <Text style={styles.noTags}>—</Text>
          )}
        </View>
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    card: {
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(245,158,11,0.30)',
      backgroundColor: 'rgba(245,158,11,0.06)',
      overflow: 'hidden',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingHorizontal: spacing.sm,
      paddingVertical: 7,
      borderBottomWidth: 1,
      borderBottomColor: 'rgba(245,158,11,0.20)',
    },
    headerTitle: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    outcomeBadge: {
      backgroundColor: 'rgba(245,158,11,0.15)',
      borderRadius: radius.full,
      paddingHorizontal: 7,
      paddingVertical: 2,
    },
    outcomeBadgeText: {
      color: colors.warning,
      fontSize: 10,
      fontWeight: '700',
    },
    juryVerdictBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: spacing.sm,
      paddingVertical: 5,
      borderBottomWidth: 1,
      borderBottomColor: 'rgba(245,158,11,0.20)',
    },
    juryVerdictFavor: { backgroundColor: 'rgba(52,211,153,0.08)' },
    juryVerdictAgainst: { backgroundColor: 'rgba(248,113,113,0.08)' },
    juryVerdictText: { fontSize: 11.5, fontWeight: '700' },
    split: {
      flexDirection: 'row',
    },
    side: {
      flex: 1,
      padding: spacing.sm,
      gap: 5,
    },
    sideLeft: {
      borderRightWidth: 0,
    },
    sideRight: {},
    divider: {
      width: 1,
      backgroundColor: 'rgba(245,158,11,0.20)',
    },
    sideName: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    roleHint: {
      color: colors.textMuted,
      fontSize: 10,
      marginBottom: 2,
    },
    verdictPill: {
      alignSelf: 'flex-start',
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: 8,
      marginBottom: 2,
    },
    verdict_right: { backgroundColor: 'rgba(52,211,153,0.15)' },
    verdict_wrong: { backgroundColor: 'rgba(248,113,113,0.15)' },
    verdict_neutral: { backgroundColor: colors.surfaceAlt },
    verdictText: {
      fontSize: 11,
      fontWeight: '700',
    },
    verdictText_right: { color: colors.success },
    verdictText_wrong: { color: colors.danger },
    verdictText_neutral: { color: colors.textMuted },
    tagPill: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: 8,
      alignSelf: 'flex-start',
    },
    tagText: {
      color: colors.text,
      fontSize: 11,
      fontWeight: '500',
    },
    noTags: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
  });
}
